// Исправления для находок полной проверки (sto_check по DOCX): находка привязана к узлу редактора (node), и
// если для этого узла живая проверка знает исправление того же вида - предлагаем его; иначе - типовые действия
// (типографика абзаца, добавить структурный элемент, открыть данные работы / источник).
import type { Ctx, LintIssue } from '../../core/registry';
import type { Finding } from '../../core/types';
import { numberingStore } from '../../core/editor/numberingPlugin';
import { scanDoc, applyFix } from './inlineLint';
import { applyTypography } from './typography';
import { insertStructElement } from './structure';
import { fillKeywords } from './keywords';

export interface FindingFix { label: string; run: (ctx: Ctx) => void }

/** Коды живой проверки, соответствующие коду sto_check. */
const FAMILY: Record<string, string[]> = {
  'FIG-CAPTION-FMT': ['CAPTION-DOT', 'CAPTION-MANUAL', 'CAPTION-CASE'],
  'TBL-CAPTION-FMT': ['CAPTION-DOT', 'CAPTION-MANUAL', 'CAPTION-CASE'],
  'LISTING-CAPTION-FMT': ['CAPTION-DOT', 'CAPTION-MANUAL', 'CAPTION-CASE'],
  'HEAD-NUM-FMT': ['HEAD-NUM-MANUAL'], 'HEAD-CASE': ['HEAD-CASE', 'HEAD-CAPS'], 'HEAD-CAPS': ['HEAD-CAPS'],
  'CITE-FMT': ['CITE-MANUAL', 'CITE-BROKEN'], 'CITE-BAD-NUM': ['CITE-BROKEN', 'CITE-MANUAL'],
  'LIST-PUNCT': ['LIST-PUNCT'], 'LIST-CASE': ['LIST-CASE'], 'LIST-MARKER': ['LIST-MARKER'], 'LIST-INTRO': ['LIST-INTRO'],
  'FOOTNOTE-DOT': ['FOOTNOTE-DOT'],
  'FIG-REF-MISSING': ['objects.refs'], 'TBL-REF-MISSING': ['objects.refs'], 'LISTING-REF-MISSING': ['objects.refs'], 'APPX-REF-MISSING': ['objects.refs'],
};

const normText = (t: string) => t.replace(/[\s\u00a0]+/g, ' ').replace(/^[-–—•]\s*/, '').trim().toLowerCase();

/** [начало, конец] узла с id в документе. */
function nodeRange(doc: any, id: string): [number, number] | null {
  let r: [number, number] | null = null;
  doc.descendants((n: any, p: number) => { if (r) return false; if (n.attrs?.id === id) { r = [p, p + n.nodeSize]; return false; } return true; });
  return r;
}

export function findingFixes(ctx: Ctx, f: Finding): FindingFix[] {
  const out: FindingFix[] = [];
  const ed = ctx.editor;
  const node = f.node;
  const code = f.code;
  if (ed && node && !node.startsWith('meta_')) {
    const codes = new Set([code, ...(FAMILY[code] || [])]);
    // замечание живой проверки того же вида - у самого узла или внутри него (позиция перечисления внутри списка:
    // sto_check привязывает её к списку); из нескольких - то, чей абзац начинается с текста находки
    const range = nodeRange(ed.state.doc, node);
    const want = normText(String(f.loc?.text || '')).slice(0, 24);
    const cands = scanDoc(ed.state.doc, numberingStore.get()).filter((x) => codes.has(x.code) && x.fixes[0] &&
      (x.node === node || (range && x.from >= range[0] && x.from <= range[1])));
    const para = (x: { from: number }) => { try { return normText(ed.state.doc.resolve(x.from).parent.textContent); } catch { return ''; } };
    const best = (want && cands.find((x) => para(x).startsWith(want))) || cands.find((x) => x.node === node) || (cands.length === 1 ? cands[0] : undefined);
    if (best) out.push({ label: best.fixes[0].label, run: (c) => { if (c.editor) applyFix(c.editor, best); } });
    if (!out.length && codes.has('objects.refs')) {
      const li = (ctx.store.getState().lint as LintIssue[]).find((l) => l.node === node && l.fix);
      if (li?.fix) out.push({ label: li.fix.label, run: li.fix.run });
    }
    if (!out.length && code.startsWith('TYPO-')) out.push({ label: 'Исправить типографику абзаца', run: (c) => { if (c.editor) applyTypography(c.editor, new Set([node])); } });
  }
  const add = (kind: string, label: string) => out.push({ label, run: (c) => { if (c.editor) insertStructElement(c.editor, kind); } });
  if (code === 'STRUCT-INTRO-MISSING') add('intro', 'Добавить «Введение»');
  if (code === 'STRUCT-SRC-MISSING') add('sources', 'Добавить список источников');
  if (code === 'STRUCT-CONCL-MISSING') {
    const t = ctx.store.getState().meta?.type || 'lab';
    add(['lab', 'pr', 'kr'].includes(t) ? 'conclusions' : 'conclusion', ['lab', 'pr', 'kr'].includes(t) ? 'Добавить «Выводы»' : 'Добавить «Заключение»');
  }
  if (code === 'STRUCT-TASK-MISSING') out.push({ label: 'Включить «Задание»', run: (c) => c.store.getState().setMeta((m) => ({ ...m, zadanie: { ...m.zadanie, enabled: true } })) });
  if (code === 'STRUCT-ABSTRACT-MISSING') out.push({ label: 'Включить «Реферат»', run: (c) => c.store.getState().setMeta((m) => ({ ...m, referat: { ...m.referat, enabled: true } })) });
  if (/KEYWORDS/.test(code) && !(ctx.store.getState().meta?.keywords || []).length) out.push({ label: 'Подобрать ключевые слова', run: (c) => { void fillKeywords(c); } });
  if (/KEYWORDS/.test(code)) out.push({ label: 'Указать ключевые слова', run: (c) => { void c.openDialog('sto.meta', { tab: 'work', field: 'keywords' }); } });
  if (code.startsWith('AI-')) out.push({ label: 'Раздел об ИИ…', run: (c) => { void c.openDialog('sto.meta', { tab: 'ai' }); } });
  if (code.startsWith('TITLE-') || code === 'META-TITLE' || code === 'META-AUTHOR' || code === 'META-SUBJECT') out.push({ label: 'Данные титула…', run: (c) => { void c.openDialog('sto.meta', { tab: 'work' }); } });
  if (code.startsWith('SRC-') || code === 'CITE-NO-LIST') out.push({ label: 'Библиотека источников', run: (c) => { const p = c.store.getState().panels; c.store.getState().set({ panels: { ...p, right: 'sources' } }); } });
  return out;
}
